import React, {MouseEvent, TouchEvent, useEffect, useRef} from "react";
import {subscribeToNotesProgressEvents} from "../../../services/eventBus/notes/notesEventBus";
import {NoteProgressChangedTypes} from "../../../services/eventBus/notes/NotesEventTypes";
import ILayoutCoordinates from "../../../models/view/keyboard/ILayoutCoordinates";
import {soundSliceSdk_seek as iframeSeek} from "../../../services/eventBus/soundslice/soundSliceSdk";
import {isKnownDuration} from "../../../util/number-util";

const style = {
  progressBarGradientStartColor: '#F7F7F7',
  progressBarGradientEndColor: '#E8E8E8',
  completedLineHeightPercent: .2,
};

interface IVideoProgressBarProps {
}

/**
 * The canvas needs to be its own separate component _without_ mapStateToProps, so state changes don't result in a rerender.
 * @constructor
 */
export default function ProgressBarCanvas({}: IVideoProgressBarProps) {
  const progressBarCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const durationSeconds = useRef<number>(0);
  let isMouseDown = false;
  let isMouseOver = false;
  useEffect(()=>{
    setCanvasSizeToMatchCSS(progressBarCanvasRef.current);
    drawProgressBar(progressBarCanvasRef.current, 0, isMouseOver);
    const unsubscribeFromNotesEvents = subscribeToNotesProgressEvents((message: NoteProgressChangedTypes) =>{
      switch(message.type){
        case "progressChanged":
          durationSeconds.current = message.durationSeconds;
          return drawProgressBar(progressBarCanvasRef.current, message.progressPercent, isMouseOver);
      }
    });
    return ()=> {
      unsubscribeFromNotesEvents();
    };
  }, [
    isMouseOver,
  ]);
  return (
    <canvas ref={progressBarCanvasRef}
      onMouseDown={() => {isMouseDown = true;}}
      onTouchStart={() => {isMouseDown = true;}}
      onMouseUp={(e) => {isMouseDown = false; seekBasedOnMouseLocation(e, durationSeconds.current); }}
      onTouchEnd={(e) => {isMouseDown = false; seekBasedOnTouchLocation(e, durationSeconds.current); }}
      onMouseMove={(e) => handleMouseMove(e, isMouseDown, durationSeconds.current) }
      onTouchMove={(e) => handleTouchMove(e, isMouseDown, durationSeconds.current) }
      onMouseEnter={() => isMouseOver=true}
      onMouseLeave={() => {isMouseDown=false; isMouseOver=false;}}
      onDragStart={ () => false}/>
  );
}

function handleMouseMove(e: MouseEvent<HTMLCanvasElement>, isMouseDown: boolean, durationSeconds: number){
  if(!isMouseDown){ return; }
  seekBasedOnMouseLocation(e, durationSeconds);
}

function handleTouchMove(e: TouchEvent<HTMLCanvasElement>, isMouseDown: boolean, durationSeconds: number){
  if(!isMouseDown){ return; }
  seekBasedOnTouchLocation(e, durationSeconds);
}

function seekBasedOnTouchLocation(e: TouchEvent<HTMLCanvasElement>, durationSeconds: number){
  if(!isKnownDuration(durationSeconds)) {return;}
  if(e.touches.length <= 0) {return;}
  const touch = e.touches[0];
  const {clientX, clientY} = touch;
  seekBasedOnLocation(clientX, clientY, e.currentTarget, durationSeconds);
}

function seekBasedOnMouseLocation(e: MouseEvent<HTMLCanvasElement>, durationSeconds: number){
  if(!isKnownDuration(durationSeconds)) {return;}
  const {clientX, clientY} = e;
  seekBasedOnLocation(clientX, clientY, e.currentTarget, durationSeconds);
}

function seekBasedOnLocation(clientX: number, clientY: number, canvas: HTMLCanvasElement, durationSeconds: number){
  const {left, width} = canvas.getBoundingClientRect();
  const x = clientX - left;
  const percentageXClickLocation = x / width;
  let seekToSeconds = durationSeconds * percentageXClickLocation;
  if(seekToSeconds < 0){
    seekToSeconds = 0;
  }
  if(seekToSeconds > durationSeconds){
    seekToSeconds = durationSeconds;
  }
  iframeSeek(seekToSeconds);

}

function drawProgressBar(canvas: HTMLCanvasElement, progressPercent: number, isMouseOver: boolean){


  requestAnimationFrame(()=>{
    setCanvasSizeToMatchCSS(canvas);
    const completedLineLayoutCoordinates = calculateProgressBarCoordinates(canvas.width, canvas.height, canvas.height * style.completedLineHeightPercent, progressPercent);
    const ctx = canvas.getContext('2d');
    if(!ctx) { return; }

    drawCompletedLine(completedLineLayoutCoordinates, ctx);
    if(isMouseOver){
      drawBall(completedLineLayoutCoordinates, ctx);
    }
  });
}

export function drawCompletedLine(layoutCoordinates: ILayoutCoordinates, ctx: CanvasRenderingContext2D){
  const {topRight, topLeft, bottomLeft, bottomRight, height, width} = layoutCoordinates;

  const gradient = ctx.createLinearGradient(topLeft.x, topRight.y, bottomLeft.x , bottomRight.y); //horizontal gradient by only having a difference in x
  gradient.addColorStop(0, style.progressBarGradientStartColor);
  gradient.addColorStop(1, style.progressBarGradientEndColor);

  ctx.fillStyle = gradient;
  ctx.fillRect(topLeft.x, topLeft.y, width, height);
}

function calculateProgressBarCoordinates(canvasWidth: number, canvasHeight: number, barHeight: number, progressPercent: number): ILayoutCoordinates{
  const canvasCenter = canvasHeight / 2;
  const barCenter = barHeight / 2;
  const startX = 0;
  const startY = canvasCenter - barCenter;
  const width = progressPercent * canvasWidth;
  const topLeft = {x: startX, y: startY};
  const topRight = {x: startX + width, y: startY};
  const bottomLeft = {x: startX, y: startY + barHeight};
  const bottomRight = {x: startX + width, y: startY + barHeight};
  return {topLeft, topRight, bottomLeft, bottomRight, width, height: barHeight};
}

function drawBall(completedLineLayoutCoordinates: ILayoutCoordinates, ctx: CanvasRenderingContext2D){
  const {bottomRight: lineTopRight, height: lineHeight} = completedLineLayoutCoordinates;
  const centerX = lineTopRight.x;
  const centerY = lineTopRight.y - (lineHeight / 2);
  const radius = lineHeight;

  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);

  const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
  gradient.addColorStop(0, style.progressBarGradientStartColor);
  gradient.addColorStop(1, style.progressBarGradientEndColor);

  ctx.fillStyle = gradient;
  ctx.fill();

  ctx.lineWidth = .6;
  ctx.strokeStyle = `rgba(0, 14, 0, .5)`;
  ctx.stroke();
}

/**
 * Canvas uses 2 distinct sets of height and width.
 * The first, which is set by attribute only, is the pixel width.
 * The second, which is set by css, is the display width.
 * We want these to match, but leave control of styling in the css world.
 * https://stackoverflow.com/questions/10214873/make-canvas-as-wide-and-as-high-as-parent
 *
 * We also need to factor in window.devicePixelRatio, so that our canvas isn't blurry.
 */
function setCanvasSizeToMatchCSS(canvas: HTMLCanvasElement){
  if(!canvas){ return; }
  canvas.width  = canvas.offsetWidth * window.devicePixelRatio; //https://stackoverflow.com/questions/24332639/why-context2d-backingstorepixelratio-deprecated
  canvas.height = canvas.offsetHeight * window.devicePixelRatio;
  return {height: canvas.height, width: canvas.width};
}
